// Donor Data Store using Vue 3 Composition API
// Global donor store
const useDonorStore = () => {
  const { ref, computed } = Vue;
  
  // Configuration
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  const STORAGE_KEY = 'louies_trust_donors';
  const TOTALS_KEY = 'louies_trust_totals';
  const storefrontAccessToken = '74efe19b346eb9a39a40029c9058211d';

  // State
  const donors = ref([]);
  const loading = ref(false);
  const error = ref(null);
  const lastFetch = ref(null);
  
  // Initialize from localStorage if available
  const initializeFromStorage = () => {
    try {
      const storedData = localStorage.getItem(STORAGE_KEY);
      if (storedData) {
        const parsed = JSON.parse(storedData);
        if (parsed.donors && parsed.timestamp) {
          const timeSinceCache = Date.now() - parsed.timestamp;
          if (timeSinceCache < CACHE_DURATION) {
            donors.value = parsed.donors;
            lastFetch.value = parsed.timestamp;
            return true; // Data was loaded from cache
          }
        }
      }
    } catch (e) {
      console.warn('Failed to load donors from localStorage:', e);
    }
    return false; // No valid cache found
  };

  // Sanitize donor data for localStorage (remove sensitive information)
  const sanitizeDonorForStorage = (donor) => {
    // Only keep fields needed for display and calculations
    // Exclude sensitive data like email, phone, address, etc.
    const {
      id,
      name,
      amount,
      date,
      gift_aid,
      anonymous,
      company,
      avatar,
      tribute,
      comment,
      fundraiser_id,
      fundraiser,
      fundraiser_link,
      fundraiser_picture,
      campaign
    } = donor;
    
    return {
      id,
      name,
      amount,
      date,
      gift_aid,
      anonymous,
      company,
      avatar,
      tribute,
      comment,
      fundraiser_id,
      fundraiser,
      fundraiser_link,
      fundraiser_picture,
      campaign
    };
  };

  // Save to localStorage
  const saveToStorage = (donorData) => {
    try {
      // Sanitize donor data before storing
      const sanitizedDonors = donorData.map(sanitizeDonorForStorage);
      
      const dataToStore = {
        donors: sanitizedDonors,
        timestamp: Date.now()
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToStore));
      
      // Also save quick totals for instant access
      const totals = {
        totalAmount: calculateTotal(donorData),
        totalWithGiftAid: calculateTotalWithGiftAid(donorData),
        donorCount: donorData.length,
        timestamp: Date.now()
      };
      localStorage.setItem(TOTALS_KEY, JSON.stringify(totals));
    } catch (e) {
      console.warn('Failed to save donors to localStorage:', e);
    }
  };

  // Get cached totals instantly (for immediate display)
  const getCachedTotals = () => {
    try {
      const storedTotals = localStorage.getItem(TOTALS_KEY);
      if (storedTotals) {
        const parsed = JSON.parse(storedTotals);
        const timeSinceCache = Date.now() - parsed.timestamp;
        if (timeSinceCache < CACHE_DURATION) {
          return parsed;
        }
      }
    } catch (e) {
      console.warn('Failed to load totals from localStorage:', e);
    }
    return null;
  };

  // Helper functions for calculations
  const calculateTotal = (donorData) => {
    return donorData.reduce((total, donor) => {
      return total + parseFloat(donor.amount || 0);
    }, 0);
  };

  const calculateTotalWithGiftAid = (donorData) => {
    const april16_2025 = new Date('2025-04-16');
    return donorData.reduce((total, donor) => {
      let amount = parseFloat(donor.amount || 0);
      
      // Add gift aid if applicable
      if (donor.gift_aid === 'true') {
        const donationDate = new Date(donor.date);
        if (donationDate >= april16_2025) {
          amount += amount * 0.25; // 25% gift aid
        }
      }
      
      return total + amount;
    }, 0);
  };

  // Initialize from storage on creation
  const hasStoredData = initializeFromStorage();

  // GraphQL pagination function
  const fetchAllDonors = async () => {
    let allDonors = [];
    let hasNextPage = true;
    let cursor = null;

    try {
      while (hasNextPage) {
        const query = `
          query${cursor ? '($cursor: String!)' : ''} {
            metaobjects(type: "donor", first: 250${cursor ? ', after: $cursor' : ''}) {
              edges {
                node {
                  id
                  fields {
                    key
                    value
                  }
                }
              }
              pageInfo {
                hasNextPage
                endCursor
              }
            }
          }
        `;

        const variables = cursor ? { cursor } : {};

        const response = await fetch('https://0d335b-4.myshopify.com/api/2023-04/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
          },
          body: JSON.stringify({
            query,
            variables
          }),
        });

        if (!response.ok) {
          throw new Error(`Network response was not ok: ${response.statusText}`);
        }

        const responseJson = await response.json();
        
        if (responseJson.errors) {
          throw new Error(`GraphQL errors: ${JSON.stringify(responseJson.errors)}`);
        }

        if (!responseJson.data || !responseJson.data.metaobjects) {
          throw new Error('No data returned from GraphQL query');
        }

        const { edges, pageInfo } = responseJson.data.metaobjects;
        allDonors = allDonors.concat(edges);
        
        hasNextPage = pageInfo.hasNextPage;
        cursor = pageInfo.endCursor;
      }

      return allDonors;
    } catch (error) {
      throw error;
    }
  };

  // Parse donor data from GraphQL response
  const parseDonorData = (donorNode) => {
    const donor = {};
    donorNode.fields.forEach(field => {
      donor[field.key] = field.value;
    });
    return donor;
  };

  // Load donors (with caching and storage)
  const loadDonors = async (forceRefresh = false) => {
    // If we have stored data and it's fresh, use it
    if (!forceRefresh && hasStoredData && donors.value.length > 0) {
      return donors.value;
    }

    // Check if we have cached data that's still fresh
    if (!forceRefresh && lastFetch.value && donors.value.length > 0) {
      const timeSinceLastFetch = Date.now() - lastFetch.value;
      if (timeSinceLastFetch < CACHE_DURATION) {
        return donors.value;
      }
    }

    loading.value = true;
    error.value = null;

    try {
      const donorEdges = await fetchAllDonors();
      const parsedDonors = donorEdges.map(edge => parseDonorData(edge.node));
      
      // Sort by date, newest first
      parsedDonors.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateB - dateA;
      });

      donors.value = parsedDonors;
      lastFetch.value = Date.now();
      
      // Save to localStorage for next time
      saveToStorage(parsedDonors);
      
      return parsedDonors;
    } catch (err) {
      error.value = err.message;
      console.error('Error loading donors:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  // Computed properties for different use cases
  const totalDonationAmount = computed(() => {
    return donors.value.reduce((total, donor) => {
      return total + parseFloat(donor.amount || 0);
    }, 0);
  });

  const donorCount = computed(() => donors.value.length);

  // Filter donors by fundraiser
  const getDonorsByFundraiser = (fundraiserId) => {
    return computed(() => {
      if (!fundraiserId) return donors.value;
      return donors.value.filter(donor => donor.fundraiser_id === fundraiserId);
    });
  };

  // Get donors with gift aid after specific date
  const getDonorsWithGiftAid = computed(() => {
    const april16_2025 = new Date('2025-04-16');
    return donors.value.filter(donor => {
      if (donor.gift_aid !== 'true') return false;
      const donationDate = new Date(donor.date);
      return donationDate >= april16_2025;
    });
  });

  // Calculate total with gift aid
  const totalWithGiftAid = computed(() => {
    const april16_2025 = new Date('2025-04-16');
    return donors.value.reduce((total, donor) => {
      let amount = parseFloat(donor.amount || 0);
      
      // Add gift aid if applicable
      if (donor.gift_aid === 'true') {
        const donationDate = new Date(donor.date);
        if (donationDate >= april16_2025) {
          amount += amount * 0.25; // 25% gift aid
        }
      }
      
      return total + amount;
    }, 0);
  });

  return {
    // State
    donors,
    loading,
    error,
    lastFetch,
    
    // Methods
    loadDonors,
    getDonorsByFundraiser,
    getCachedTotals,
    
    // Computed
    totalDonationAmount,
    totalWithGiftAid,
    donorCount,
    getDonorsWithGiftAid
  };
};

// Global store instance
window.donorStore = useDonorStore();

// Initialize on load - but only fetch if we don't have cached data
document.addEventListener('DOMContentLoaded', () => {
  const cachedTotals = window.donorStore.getCachedTotals();
  if (!cachedTotals) {
    // No cached data, fetch immediately
    window.donorStore.loadDonors();
  } else {
    // We have cached data, fetch in background after a short delay
    setTimeout(() => {
      window.donorStore.loadDonors();
    }, 1000); // 1 second delay to let the page load smoothly
  }
});